import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EvaluationStatus } from '@/types/evaluation.types';
import { Filter, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export type SortOption =
  | 'nameAsc'
  | 'nameDesc'
  | 'dateNewest'
  | 'dateOldest'
  | 'targetDateSoonest'
  | 'targetDateLatest'
  | 'solutionCountHigh'
  | 'solutionCountLow';

interface FilterBarProps {
  selectedStatuses: EvaluationStatus[];
  onStatusToggle: (status: EvaluationStatus) => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export function FilterBar({
  selectedStatuses,
  onStatusToggle,
  sortBy,
  onSortChange,
  onClearFilters,
  hasActiveFilters,
}: FilterBarProps) {
  const { t } = useTranslation('home');

  const allStatuses = [
    EvaluationStatus.Organizing,
    EvaluationStatus.Active,
    EvaluationStatus.Concluded,
    EvaluationStatus.Closed,
  ];

  const isStatusSelected = (status: EvaluationStatus) =>
    selectedStatuses.includes(status);

  const allSelected = selectedStatuses.length === allStatuses.length;
  const activeFilterCount = allSelected ? 0 : selectedStatuses.length;

  return (
    <div className="flex gap-2 items-center">
      {/* Status Filter Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9">
            <Filter className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('filters.label')}
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 px-1 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuLabel>{t('filters.label')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {allStatuses.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={isStatusSelected(status)}
              onCheckedChange={() => onStatusToggle(status)}
              onSelect={(e) => e.preventDefault()}
            >
              {t(`common:status.${status.toLowerCase()}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Sort Dropdown */}
      <Select value={sortBy} onValueChange={onSortChange}>
        <SelectTrigger className="h-9 w-[180px]" aria-label={t('sort.label')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="dateNewest">{t('sort.dateNewest')}</SelectItem>
          <SelectItem value="dateOldest">{t('sort.dateOldest')}</SelectItem>
          <SelectItem value="nameAsc">{t('sort.nameAsc')}</SelectItem>
          <SelectItem value="nameDesc">{t('sort.nameDesc')}</SelectItem>
          <SelectItem value="targetDateSoonest">
            {t('sort.targetDateSoonest')}
          </SelectItem>
          <SelectItem value="targetDateLatest">
            {t('sort.targetDateLatest')}
          </SelectItem>
          <SelectItem value="solutionCountHigh">
            {t('sort.solutionCountHigh')}
          </SelectItem>
          <SelectItem value="solutionCountLow">
            {t('sort.solutionCountLow')}
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="h-9 text-muted-foreground hover:text-foreground"
          aria-label={t('filters.clearFilters')}
        >
          <X className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">{t('filters.clearFilters')}</span>
        </Button>
      )}
    </div>
  );
}
